import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  controleerAfsluiten,
  maakEvenementcijfersUitTelling,
  maakWaarschuwingen,
  maakInlogcode,
  type Afsluitcontrole,
  type UurTelling,
  type Evenementcijfers,
  type Kassastand,
  type KraamInvoer,
  type Waarschuwing,
} from '@qc/core';
import type { Evenement, Kraamdeelname, MenuItem } from '@qc/db';
import { haalAlles } from '../paginering.js';
import { supabase } from '../supabase.js';

/** Hoe vaak het dashboard bijwerkt. De eis is: nooit meer dan een minuut achter. */
const VERVERS_MS = 20_000;

export interface KraamOpEvenement {
  deelname: Kraamdeelname;
  kraamnaam: string;
  kraamId: string;
}

export interface MenuTerGoedkeuring {
  menuId: string;
  kraamdeelnameId: string;
  kraamnaam: string;
  versie: number;
  items: MenuItem[];
}

export interface EvenementBeeld {
  laden: boolean;
  fout: string | null;
  bijgewerktOp: Date | null;
  kramen: KraamOpEvenement[];
  cijfers: Evenementcijfers;
  afsluitcontrole: Afsluitcontrole;
  /** Waar de organisator nú iets van moet weten. */
  waarschuwingen: Waarschuwing[];
  menusTerGoedkeuring: MenuTerGoedkeuring[];
  producten: MenuItem[];
  uitverkocht: Set<string>;
  /** Geeft de zojuist opgehaalde kassastanden terug — nodig om er direct op
   * te controleren zonder op de volgende render te wachten (zie sluitAf). */
  ververs: () => Promise<Kassastand[] | undefined>;
  nodigKraamUit: (
    naam: string,
    email: string,
    commissieBasispunten: number,
  ) => Promise<{ code: string; gemaild: boolean } | null>;
  /** Mailt de uitnodiging (opnieuw) naar het e-mailadres van de kraam. */
  mailUitnodiging: (kraamdeelnameId: string) => Promise<{ ok: boolean; melding: string }>;
  zetCommissie: (kraamdeelnameId: string, basispunten: number) => Promise<void>;
  zetKraamstatus: (kraamdeelnameId: string, actief: boolean) => Promise<void>;
  blokkeerProduct: (menuItemId: string, blokkeren: boolean) => Promise<void>;
  keurMenu: (menuId: string, goedkeuren: boolean) => Promise<void>;
  sluitAf: () => Promise<boolean>;
}

export function useEvenement(evenement: Evenement): EvenementBeeld {
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState<string | null>(null);
  const [bijgewerktOp, setBijgewerktOp] = useState<Date | null>(null);
  const [kramen, setKramen] = useState<KraamOpEvenement[]>([]);
  const [telling, setTelling] = useState<UurTelling[]>([]);
  const [standen, setStanden] = useState<Kassastand[]>([]);
  const [menusTerGoedkeuring, setMenus] = useState<MenuTerGoedkeuring[]>([]);
  const [producten, setProducten] = useState<MenuItem[]>([]);
  const [uitverkocht, setUitverkocht] = useState<Set<string>>(new Set());

  const ververs = useCallback(async (): Promise<Kassastand[] | undefined> => {
    const { data: deelnames, error: deelnamefout } = await supabase
      .from('kraamdeelnames')
      .select('*')
      .eq('evenement_id', evenement.id);

    if (deelnamefout) {
      setFout('De gegevens konden niet worden opgehaald.');
      setLaden(false);
      return undefined;
    }

    const deelnameLijst = deelnames ?? [];
    const kraamIds = deelnameLijst.map((deelname) => deelname.kraam_id);
    const deelnameIds = deelnameLijst.map((deelname) => deelname.id);

    const { data: kraamrijen } = await supabase
      .from('kramen')
      .select('id, naam')
      .in('id', kraamIds.length > 0 ? kraamIds : ['00000000-0000-0000-0000-000000000000']);

    const naamPerKraam = new Map((kraamrijen ?? []).map((kraam) => [kraam.id, kraam.naam]));

    const opgebouwd: KraamOpEvenement[] = deelnameLijst.map((deelname) => ({
      deelname,
      kraamId: deelname.kraam_id,
      kraamnaam: naamPerKraam.get(deelname.kraam_id) ?? 'Kraam',
    }));
    setKramen(opgebouwd);

    if (deelnameIds.length === 0) {
      setTelling([]);
      setStanden([]);
      setMenus([]);
      setProducten([]);
      setLaden(false);
      setBijgewerktOp(new Date());
      setFout(null);
      return [];
    }

    // De database telt de bonnen zelf op, per kraam en per uur (functie evenement_live). Alle
    // bonnen ophalen werkt niet op een groot evenement: de server geeft hoogstens duizend rijen
    // per verzoek, en bij 16.000 bonnen duurt het lezen langs de beveiligingsregels een halve
    // minuut terwijl dit scherm elke 20 seconden ververst.
    const [{ data: tellingrijen, error: tellingFout }, { data: standrijen }, { data: menurijen }] =
      await Promise.all([
        supabase.rpc('evenement_live', { p_evenement: evenement.id }),
        supabase.from('kassastanden').select('*').in('kraamdeelname_id', deelnameIds),
        supabase
          .from('menus')
          .select('id, kraamdeelname_id, versie, status')
          .in('kraamdeelname_id', deelnameIds),
      ]);

    // Zonder telling klopt de omzet niet: liever een melding dan een te laag bedrag tonen.
    if (tellingFout) {
      setFout('De omzet kon niet worden opgehaald.');
      setLaden(false);
      return undefined;
    }

    setTelling(
      (tellingrijen ?? []).map((rij) => ({
        kraamdeelnameId: rij.kraamdeelname_id,
        uur: rij.uur,
        aantalBonnen: Number(rij.aantal_bonnen),
        totaalCent: Number(rij.totaal_cent),
        statiegeldCent: Number(rij.statiegeld_cent),
      })),
    );

    // Eén regel per toestel, niet per kraam: draait een kraam met twee
    // toestellen dan moeten ze allebei klaar zijn voordat er afgesloten wordt.
    const standenPerDeelname = new Map<string, typeof standrijen>();
    for (const stand of standrijen ?? []) {
      const lijst = standenPerDeelname.get(stand.kraamdeelname_id) ?? [];
      lijst.push(stand);
      standenPerDeelname.set(stand.kraamdeelname_id, lijst);
    }

    // Als lokale waarde vasthouden en teruggeven, niet alleen in state zetten:
    // sluitAf moet hier direct op kunnen controleren binnen dezelfde aanroep.
    // React's setStanden werkt pas door bij de volgende render — daar leest
    // een lopende closure niet uit.
    const nieuweStanden = opgebouwd.flatMap((kraam): Kassastand[] => {
      const eigen = standenPerDeelname.get(kraam.deelname.id) ?? [];
      const isActief = kraam.deelname.status === 'actief';

      if (eigen.length === 0) {
        return [
          {
            kraamdeelnameId: kraam.deelname.id,
            kraamnaam: kraam.kraamnaam,
            wachtendeBonnen: 0,
            laatstGezien: null,
            isActief,
            vastgelopen: 0,
          },
        ];
      }

      return eigen.map((stand, index) => ({
        kraamdeelnameId: kraam.deelname.id,
        kraamnaam: kraam.kraamnaam,
        wachtendeBonnen: stand.wachtende_bonnen,
        laatstGezien: stand.laatst_gezien,
        isActief,
        vastgelopen: stand.vastgelopen,
        ...(eigen.length > 1 ? { toestelnaam: `toestel ${index + 1}` } : {}),
      }));
    });
    setStanden(nieuweStanden);

    const alleMenus = menurijen ?? [];
    const menuIds = alleMenus.map((menu) => menu.id);
    const items = await haalAlles<MenuItem>((van, tot) =>
      supabase
        .from('menu_items')
        .select('*')
        .in('menu_id', menuIds.length > 0 ? menuIds : ['00000000-0000-0000-0000-000000000000'])
        .order('volgorde')
        .order('id')
        .range(van, tot),
    );
    const naamPerDeelname = new Map(opgebouwd.map((kraam) => [kraam.deelname.id, kraam.kraamnaam]));

    setMenus(
      alleMenus
        .filter((menu) => menu.status === 'ter_goedkeuring')
        .map((menu) => ({
          menuId: menu.id,
          kraamdeelnameId: menu.kraamdeelname_id,
          kraamnaam: naamPerDeelname.get(menu.kraamdeelname_id) ?? 'Kraam',
          versie: menu.versie,
          items: items.filter((item) => item.menu_id === menu.id),
        })),
    );

    const goedgekeurdeMenuIds = new Set(
      alleMenus.filter((menu) => menu.status === 'goedgekeurd').map((menu) => menu.id),
    );
    const actieveProducten = items.filter((item) => goedgekeurdeMenuIds.has(item.menu_id));
    setProducten(actieveProducten);

    // Alleen wat uitverkocht is, en gekoppeld via het menu in plaats van met een lijst van alle
    // producten in de URL: bij 45 kramen zijn dat duizenden id's en wordt het adres te lang.
    const voorraadrijen = await haalAlles<{ menu_item_id: string }>((van, tot) =>
      supabase
        .from('voorraadstanden')
        .select('menu_item_id, menu_items!inner(menu_id)')
        .in(
          'menu_items.menu_id',
          goedgekeurdeMenuIds.size > 0
            ? [...goedgekeurdeMenuIds]
            : ['00000000-0000-0000-0000-000000000000'],
        )
        .eq('status', 'uitverkocht')
        .order('menu_item_id')
        .range(van, tot),
    );
    setUitverkocht(new Set(voorraadrijen.map((rij) => rij.menu_item_id)));

    setFout(null);
    setLaden(false);
    setBijgewerktOp(new Date());
    return nieuweStanden;
  }, [evenement.id]);

  useEffect(() => {
    void (async () => {
      await ververs();
    })();
    const tik = setInterval(() => {
      void (async () => {
        await ververs();
      })();
    }, VERVERS_MS);
    return () => clearInterval(tik);
  }, [ververs]);

  const cijfers = useMemo(() => {
    const invoer: KraamInvoer[] = kramen.map((kraam) => ({
      kraamdeelnameId: kraam.deelname.id,
      kraamnaam: kraam.kraamnaam,
      commissieBasispunten: kraam.deelname.commissie_bp,
    }));
    return maakEvenementcijfersUitTelling(invoer, telling);
  }, [telling, kramen]);

  const afsluitcontrole = useMemo(
    () => controleerAfsluiten(standen, bijgewerktOp ?? new Date()),
    [bijgewerktOp, standen],
  );

  const waarschuwingen = useMemo(
    () => maakWaarschuwingen(standen, bijgewerktOp ?? new Date()),
    [bijgewerktOp, standen],
  );

  const nodigKraamUit = useCallback(
    async (naam: string, email: string, commissieBasispunten: number) => {
      // Het id maken we zelf en we lezen het niet terug: een kraam die nog aan
      // geen enkel evenement hangt is voor niemand zichtbaar, ook niet voor
      // wie hem net heeft aangemaakt. Terugleggen zou dus altijd mislukken.
      const kraamId = crypto.randomUUID();

      const { error: kraamfout } = await supabase
        .from('kramen')
        .insert({ id: kraamId, naam, email: email === '' ? null : email });

      if (kraamfout) {
        setFout('De kraam kon niet worden aangemaakt.');
        return null;
      }

      const inlogcode = maakInlogcode({ kraamnaam: naam });
      const { data: deelnameRij, error: deelnamefout } = await supabase
        .from('kraamdeelnames')
        .insert({
          evenement_id: evenement.id,
          kraam_id: kraamId,
          commissie_bp: commissieBasispunten,
          status: 'uitgenodigd',
          inlogcode,
        })
        .select('id')
        .single();

      if (deelnamefout || deelnameRij === null) {
        setFout('De kraam is aangemaakt, maar uitnodigen lukte niet.');
        return null;
      }

      // De rij hierboven staat er, maar de kraam kan er nog niet mee
      // inloggen: dat vraagt een echt Supabase Auth-account, dat alleen
      // server-side (met de service-rolsleutel) aangemaakt kan worden.
      const { data: uitslag, error: inlogfout } = await supabase.functions.invoke<{
        gemaild?: boolean;
      }>('nodig-kraam-uit', {
        body: { kraamdeelnameId: deelnameRij.id },
      });
      if (inlogfout) {
        setFout(
          'De kraam staat op het evenement, maar het inloggen kon niet worden aangemaakt. Probeer het zo nog eens, of vraag support.',
        );
        return null;
      }

      await ververs();
      return { code: inlogcode, gemaild: uitslag?.gemaild === true };
    },
    [evenement.id, ververs],
  );

  const mailUitnodiging = useCallback(async (kraamdeelnameId: string) => {
    const { data, error } = await supabase.functions.invoke<{
      gemaild?: boolean;
      melding?: string;
    }>('nodig-kraam-uit', { body: { kraamdeelnameId } });
    if (error) {
      return {
        ok: false,
        melding: 'De uitnodiging kon niet worden verstuurd. Probeer het zo nog eens.',
      };
    }
    if (data?.gemaild !== true) {
      return { ok: false, melding: data?.melding ?? 'De uitnodiging kon niet worden gemaild.' };
    }
    return { ok: true, melding: data.melding ?? 'De uitnodiging is gemaild.' };
  }, []);

  const zetCommissie = useCallback(
    async (kraamdeelnameId: string, basispunten: number) => {
      const { error } = await supabase
        .from('kraamdeelnames')
        .update({ commissie_bp: basispunten })
        .eq('id', kraamdeelnameId);
      // Zonder deze controle denkt de organisator dat de commissie is
      // aangepast terwijl de oude waarde gewoon in de afrekening terechtkomt.
      if (error) {
        setFout('De commissie kon niet worden aangepast.');
        return;
      }
      await ververs();
    },
    [ververs],
  );

  const zetKraamstatus = useCallback(
    async (kraamdeelnameId: string, actief: boolean) => {
      const { error } = await supabase
        .from('kraamdeelnames')
        .update({ status: actief ? 'actief' : 'gepauzeerd' })
        .eq('id', kraamdeelnameId);
      // Dit is de pauzeerknop: mislukt hij stilletjes, dan denkt de organisator
      // dat een probleemkraam stilligt terwijl hij gewoon doorverkoopt.
      if (error) {
        setFout('De kraam kon niet worden gepauzeerd of hervat.');
        return;
      }
      await ververs();
    },
    [ververs],
  );

  const blokkeerProduct = useCallback(
    async (menuItemId: string, blokkeren: boolean) => {
      const { error } = await supabase.from('voorraadstanden').upsert(
        {
          menu_item_id: menuItemId,
          status: blokkeren ? 'uitverkocht' : 'beschikbaar',
          resterend: blokkeren ? 0 : null,
          bijgewerkt_op: new Date().toISOString(),
        },
        { onConflict: 'menu_item_id' },
      );
      if (error) {
        setFout('Het product kon niet worden geblokkeerd of vrijgegeven.');
        return;
      }
      await ververs();
    },
    [ververs],
  );

  const keurMenu = useCallback(
    async (menuId: string, goedkeuren: boolean) => {
      const { error } = await supabase
        .from('menus')
        .update({
          status: goedkeuren ? 'goedgekeurd' : 'concept',
          goedgekeurd_op: goedkeuren ? new Date().toISOString() : null,
        })
        .eq('id', menuId);
      if (error) {
        setFout('Het menu kon niet worden goedgekeurd.');
        return;
      }
      await ververs();
    },
    [ververs],
  );

  const sluitAf = useCallback(async () => {
    // Nog één keer verse gegevens: tussen het openen van het scherm en de klik
    // kan er een bon zijn bijgekomen. Het resultaat van ververs() direct
    // gebruiken, niet de standen-state: die is binnen déze aanroep nog niet
    // bijgewerkt — een setState-aanroep werkt pas door bij de volgende
    // render, en die komt hier nooit, want er wordt niets meer gerenderd
    // tussen de aanroep en de controle hieronder.
    const verseStanden = await ververs();
    if (verseStanden === undefined) return false;
    const opnieuw = controleerAfsluiten(verseStanden, new Date());
    if (!opnieuw.magAfsluiten) return false;

    const { error } = await supabase
      .from('evenementen')
      .update({ status: 'afgesloten', afgesloten_op: new Date().toISOString() })
      .eq('id', evenement.id);

    if (error) {
      setFout('Afsluiten lukte niet. Probeer het zo nog eens.');
      return false;
    }
    await ververs();
    return true;
  }, [evenement.id, ververs]);

  return {
    laden,
    fout,
    bijgewerktOp,
    kramen,
    cijfers,
    afsluitcontrole,
    waarschuwingen,
    menusTerGoedkeuring,
    producten,
    uitverkocht,
    ververs,
    nodigKraamUit,
    mailUitnodiging,
    zetCommissie,
    zetKraamstatus,
    blokkeerProduct,
    keurMenu,
    sluitAf,
  };
}
